import { FC, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import TableTab from "../../../components/tabs/table-tab";
import { REPORTS_FILTER_DATE_TIME_SPECIFIC_DATE, REPORTS_FILTER_TIME } from "../../../constants/element-type";
import { FILTER_DATE, FILTER_READERS, FILTER_SITES, FILTER_TIME } from "../../../constants/filter-types";
import { REPORT_STILL_ON_SITE } from "../../../constants/menu-types";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import { useLocale } from "../../../contexts/localeContext";
import { TableContextProvider } from "../../../contexts/table/TableContextProvider";
import { DEF_PG_SIZE_REPORTS } from '../../../constants/maximum-page-size';
import StillOnSiteColumn from "../../../models/ColumnDefinitions/StillOnSiteColumn";
import { Filter } from "../../../models/Filter";
import { ListQuery } from "../../../models/ListQuery";
import { Lookup } from "../../../models/Lookup";
import LookupTypes from "../../../models/LookupTypes";
import CategorySearch from "../../../models/search/CategorySearch";
import CompanySearch from "../../../models/search/CompanySearch";
import DepartmentSearch from "../../../models/search/DepartmentSearch";
import DeviceImproxSearch from "../../../models/search/DeviceImproxSearch";
import SiteSearch from "../../../models/search/SiteSearch";
import { auditRunReport } from "../../../services/auditService";
import { getCategoryNameWithPagination } from "../../../services/categoryService";
import { getCompanyNamesWithPagination } from "../../../services/companyService";
import { getDepartmentNamesWithPagination } from "../../../services/departmentService";
import { getReaderNamesWithPagination } from "../../../services/readerService";
import { getEnrolmentNamesWithPagination } from "@/services/enrolmentService";
import { getStillOnSiteReport } from "../../../services/reportService";
import { getSiteById, getSiteNamesWithPagination } from "../../../services/siteService";
import { getUserFilterPreference } from "../../../services/userPreferenceService";
import { showError } from "../../../services/utilities/ServiceError";
import { IAppState } from "../../../store/types";
import { getTodayDate } from "../../../utilities/DateUtils";
import { appendFilter } from "../../../utilities/filters";
import StillOnSiteSearch from "@/models/search/StillOnSiteSearch";

interface StiiOnSiteProps {
    reportId: string;
}

const StillOnSite: FC<StiiOnSiteProps> = ({ reportId }) => {
    const { translate } = useLocale();
    const lookup: Lookup[] = [
        {
            name: LookupTypes.companyId,
            values: [],
            dataMethod: (listQuery?: ListQuery) => getCompanyNamesWithPagination({ ...listQuery, filters: appendFilter(siteId, "Site", "site.id", listQuery?.filters ?? []) }),
            searchAccessor: CompanySearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.departmentId,
            values: [],
            dataMethod: (listQuery?: ListQuery) => getDepartmentNamesWithPagination({ ...listQuery, filters: appendFilter(siteId, "Site", "site.id", listQuery?.filters ?? []) }),
            searchAccessor: DepartmentSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.site,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName,
            isSingleSelect: true
        },
        {
            name: LookupTypes.profileId,
            values: [],
            dataMethod: getCategoryNameWithPagination,
            searchAccessor: CategorySearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: 'transacksForTrMasterId.terminal.id',
            values: [],
            dataMethod: getReaderNamesWithPagination,
            searchAccessor: DeviceImproxSearch.find(item => item.accessor === "name")?.searchOnName,
            filterDependentKey: StillOnSiteColumn.find(col => col.accessor === "site")?.searchOnName
        },
        {
            name: LookupTypes.masterId,
            values: [],
            dataMethod: getEnrolmentNamesWithPagination,
            searchAccessor: StillOnSiteSearch.find(column => column.accessor === "name")?.searchOnName
        }
    ];

    // user preference items
    const userLoginId = useSelector((state: IAppState) => (state.userInfo.id));
    const userSiteId = useSelector((state: IAppState) => (state.userInfo.siteId));
    const [siteId, setSiteId] = useState(userSiteId);

    const getOriginalStaticFilter = async (filterKey: string): Promise<Filter> => {
        switch (filterKey) {
            case "site.id":
                const site = await getSiteById(userSiteId);
                return {
                    key: filterKey,
                    label: translate.byKey(TranslationKeys.site),
                    isCustomFilter: true,
                    isStatic: true,
                    clearPolicy: "locked",
                    options: [],
                    selected: [
                        {
                            label: site.name,
                            value: site.id.toString()
                        }
                    ],
                };
            case "time":
                return {
                    key: filterKey,
                    label: translate.byKey(TranslationKeys.stillOnSite),
                    isCustomFilter: true,
                    isStatic: true,
                    clearPolicy: "locked",
                    options: [],
                    selected: [
                        {
                            label: "17:00",
                            value: "17:00",
                            elementSysname: REPORTS_FILTER_TIME
                        }
                    ],
                };
            case "dateOnly":
                return {
                    isCustomFilter: true,
                    key: filterKey,
                    label: translate.byKey(TranslationKeys.date),
                    isStatic: true,
                    clearPolicy: "locked",
                    options: [],
                    selected: [{
                        label: getTodayDate(),
                        value: getTodayDate(),
                        elementSysname: REPORTS_FILTER_DATE_TIME_SPECIFIC_DATE
                    }],
                };
            case "transacksForTrMasterId.terminal.id":
                return {
                    isCustomFilter: true,
                    key: filterKey,
                    label: translate.byKey(TranslationKeys.siteExits),
                    isStatic: true,
                    options: [],
                    selected: [{
                        ignore: true,
                        label: translate.byKey(TranslationKeys.required),
                        // This value is used to indicate that the filter is required and it is the only option we need to show the filter chip with red colors. (requrie_filter is a dummy value but required to show the filter chip with red color)
                        value: "require_filter",
                    }],
                };
            default:
                return {
                    key: filterKey,
                    label: filterKey,
                    isCustomFilter: true,
                    options: [],
                    selected: [
                        {
                            label: translate.byKey(TranslationKeys.all),
                            value: '*',
                            ignore: true
                        }
                    ],
                    isStatic: true
                }
        }
    }

    const initialiseUserPreferences = async (setDetails: (userLoginId, currentMenuDetail, filters, reportId, staticFilterKeys) => void) => {
        const staticFilterKeys = ["site.id", "time", "dateOnly", "transacksForTrMasterId.terminal.id"];
        let filters: Filter[] = [];

        try {
            const viewPreference = await getUserFilterPreference(userLoginId, REPORT_STILL_ON_SITE, reportId);
            const siteFilter = viewPreference.find(filter => filter.filterId === FILTER_SITES);
            const timeFilter = viewPreference.find(filter => filter.filterId === FILTER_TIME);
            const dateFilter = viewPreference.find(filter => filter.filterId === FILTER_DATE);
            const siteExitFilter = viewPreference.find(filter => filter.filterId === FILTER_READERS);

            if (!!!siteFilter) {
                const siteStaticFilter = await getOriginalStaticFilter("site.id");
                filters.push(siteStaticFilter);
            }
            else if (siteFilter && siteFilter.selected.length === 1 && !siteFilter.selected[0].ignore) {
                setSiteId(siteFilter.selected[0].value);
            } else {
                const siteStaticFilter = await getOriginalStaticFilter("site.id");
                filters.push(siteStaticFilter);
            }

            if (!!!timeFilter) {
                const timeStaticFilter = await getOriginalStaticFilter("time");
                filters.push(timeStaticFilter);
            }

            if (!!!dateFilter) {
                const dateStaticFilter = await getOriginalStaticFilter("dateOnly");
                filters.push(dateStaticFilter);
            }

            if (!!!siteExitFilter) {
                const siteExitStaticFilter = await getOriginalStaticFilter("transacksForTrMasterId.terminal.id");
                filters.push(siteExitStaticFilter);
            }
        }
        catch (exception) {
            showError(exception, toast.warn);
        }
        finally {
            setDetails(userLoginId, REPORT_STILL_ON_SITE, filters, reportId, staticFilterKeys);
        }
    }

    const getDataMethod = async (listParameters?: ListQuery) => {
        try {
            await auditRunReport(reportId);
            return getStillOnSiteReport(translate, toast.warn, listParameters)
        } catch (error) {
            return Promise.reject(error);
        }
    }

    return <TableContextProvider
        maxPageSize={DEF_PG_SIZE_REPORTS}
        dataMethod={getDataMethod}
        initialiser={(setDetails) => initialiseUserPreferences(setDetails)}
        tableDescriptor={StillOnSiteColumn}
        searchDescriptor={StillOnSiteSearch}
        useFilter
        useSort
        usePagination
        useSearch
        useTable
        lookup={lookup}
        getOriginalStaticFilter={getOriginalStaticFilter}
        loadDataOnDemand
        runDataOnDemandButtonLabel={translate.byKey(TranslationKeys.runReport)}>
        <TableTab
            title={''}
            reportTitle={translate.byKey(TranslationKeys.stillOnSite)}
            selectRows={false}
            cyName="stillOnSiteReport"/>
    </TableContextProvider >
}

export default StillOnSite;

